/* src/modules/peer/AchievementCard.js */
import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { 
    TrophyIcon, 
    TrendingUpIcon, 
    XCircleIcon
} from '../../components/icons/Icons';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// --- CONFIGURATION ---
const BRAND_COLORS = {
    darkBlue: '#213368',
    teal: '#20BEC6',
    orange: '#F4A866',
    gold: '#FFD700',
    lightGrey: '#F3F4F6'
};

// --- LEVEL DEFINITIONS ---
const LEVEL_TIERS = [
    { max: 50, label: "Rookie", range: "0 - 49" },
    { max: 100, label: "Rising Star", range: "50 - 99" },
    { max: 200, label: "Captain", range: "100 - 199" },   // Was Scout
    { max: 300, label: "Rockstar", range: "200 - 299" },  // Was Captain
    { max: 500, label: "Superstar", range: "300 - 499" }, // Was Champion
    { max: Infinity, label: "Legend", range: "500+" }
];

// --- HELPER: Get Level Info ---
const getLevelInfo = (points) => {
    const safePoints = points || 0;
    // Find the first tier where the user's points are less than the max
    const tier = LEVEL_TIERS.find(t => safePoints < t.max) || LEVEL_TIERS[LEVEL_TIERS.length - 1];
    return tier;
};

// --- HELPER: Date Formatter ---
const formatDate = (val) => {
    if (!val) return 'N/A';
    try {
        const date = val.toDate ? val.toDate() : new Date(val);
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } catch (e) { return 'N/A'; }
};

export default function AchievementCard({ userId, appId, onClose }) {
    const [userData, setUserData] = useState(null);
    const [trophyCount, setTrophyCount] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // 1. Fetch User Profile
                const userRef = doc(db, `artifacts/${appId}/users`, userId);
                const userSnap = await getDoc(userRef);
                
                if (userSnap.exists()) {
                    setUserData(userSnap.data());
                }

                // 2. Count Trophies (Challenges Won)
                const challRef = collection(db, `artifacts/${appId}/challenges`);
                const qWins = query(challRef, where('winners', 'array-contains', userId));
                const winSnap = await getDocs(qWins);
                setTrophyCount(winSnap.size);

            } catch (error) {
                console.error("Error fetching achievement card data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [appId, userId]);

    if (!userData && !loading) return null;

    // Calculate level info based on current points
    const levelInfo = userData ? getLevelInfo(userData.points) : LEVEL_TIERS[0];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4 animate-fadeIn backdrop-blur-sm">
            
            {/* Card Container */}
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative transform transition-all scale-100">
                
                {/* Close Button */}
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 text-white hover:text-gray-200 z-10 bg-black/20 rounded-full p-1"
                >
                    <XCircleIcon className="h-6 w-6" />
                </button>

                {loading ? (
                    <div className="p-12 flex justify-center"><LoadingSpinner /></div>
                ) : (
                    <>
                        {/* --- CARD HEADER (Gradient Background) --- */}
                        <div className="relative h-32 bg-gradient-to-r from-[#213368] to-[#20BEC6]">
                            <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2">
                                <img 
                                    src={userData.photoURL || `https://ui-avatars.com/api/?name=${userData.name}&background=random`} 
                                    alt={userData.name}
                                    referrerPolicy="no-referrer"
                                    className="h-24 w-24 rounded-full border-4 border-white shadow-md object-cover bg-white"
                                />
                            </div>
                        </div>

                        {/* --- CARD BODY --- */}
                        <div className="pt-14 pb-8 px-6 text-center">
                            
                            {/* User Name & Designation */}
                            <h2 className="text-2xl font-bold text-[#213368]">{userData.name}</h2>
                            <p className="text-gray-700 font-medium text-sm">{userData.designation || 'Team Member'}</p>
                            
                            {/* Department & Location */}
                            <div className="flex justify-center items-center space-x-2 text-sm text-gray-500 mt-1">
                                <span>{userData.department || 'General'}</span>
                                <span>•</span>
                                <span>{userData.location || 'Remote'}</span>
                            </div>

                            {/* Joining Date */}
                            <p className="text-xs text-gray-400 mt-2">
                                Member since {formatDate(userData.dateOfJoining)}
                            </p>

                            {/* --- STATS GRID --- */}
                            <div className="grid grid-cols-3 gap-4 mt-8">
                                {/* Points */}
                                <div className="p-2 bg-blue-50 rounded-xl border border-blue-100 flex flex-col justify-center">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Points</p>
                                    <p className="text-xl font-extrabold text-[#20BEC6]">{userData.points || 0}</p>
                                </div>
                                
                                {/* Level (Dynamic Name + Range) */}
                                <div className="p-2 bg-purple-50 rounded-xl border border-purple-100 flex flex-col justify-center items-center">
                                    {/* UPDATED LABEL HERE */}
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Status</p>
                                    <div className="flex flex-col items-center">
                                        <div className="flex items-center space-x-1">
                                            <TrendingUpIcon className="h-4 w-4 text-purple-500" />
                                            <p className="text-lg font-extrabold text-purple-600 leading-tight">
                                                {levelInfo.label}
                                            </p>
                                        </div>
                                        <p className="text-[10px] text-purple-400 font-medium mt-0.5">
                                            ({levelInfo.range} pts)
                                        </p>
                                    </div>
                                </div>
                                
                                {/* Trophies */}
                                <div className="p-2 bg-yellow-50 rounded-xl border border-yellow-100 flex flex-col justify-center">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Challenges Won</p>
                                    <div className="flex items-center justify-center space-x-1">
                                        <TrophyIcon className="h-4 w-4 text-yellow-600" />
                                        <p className="text-xl font-extrabold text-yellow-600">{trophyCount}</p>
                                    </div>
                                </div>
                            </div>

                            {/* --- BADGES SECTION --- */}
                            <div className="mt-8">
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Badges Collected</p>
                                <div className="flex justify-center space-x-6">
                                    <BadgeItem 
                                        count={userData.badges?.commitment || userData.badges?.Commitment || 0} 
                                        color={BRAND_COLORS.orange} 
                                        label="Commitment"
                                    />
                                    <BadgeItem 
                                        count={userData.badges?.collaboration || userData.badges?.Collaboration || 0} 
                                        color={BRAND_COLORS.teal} 
                                        label="Collaboration"
                                    />
                                    <BadgeItem 
                                        count={userData.badges?.customer || userData.badges?.Customer || 0} 
                                        color={BRAND_COLORS.red} 
                                        label="Customer"
                                    />
                                </div>
                            </div>

                        </div>
                        
                        {/* Footer Stripe */}
                        <div className="h-2 bg-gradient-to-r from-[#213368] to-[#20BEC6] w-full"></div>
                    </>
                )}
            </div>
        </div>
    );
}

// Sub-component for badges
function BadgeItem({ count, color, label }) {
    const isActive = count > 0;
    return (
        <div className={`flex flex-col items-center group relative ${!isActive ? 'opacity-50 grayscale' : ''}`}>
            <div 
                className="h-12 w-12 rounded-full flex items-center justify-center text-white font-bold shadow-sm transition transform group-hover:scale-110"
                style={{ backgroundColor: isActive ? color : '#E5E7EB' }}
            >
                {count > 0 ? (
                    <span>{count}</span>
                ) : (
                    <span className="text-gray-400 text-xs">0</span>
                )}
            </div>
            <span className="text-[10px] font-bold text-gray-500 mt-2">{label}</span>
        </div>
    );
}